import {
    waitForElement,
    simulateTyping,
    getInputValue,
    makeRequiredInputStep,
    makeBoxRequiredInputStep,
    makeRequiredClickStep
} from "@/utils/tourUtils";
import {
    AGENT_PAGE,
    AUTOMATION_FORGE,
    CLIP_PAGE_NAME, CLIP_SINGLE_PAGE, CONTENT_AGGREGATION_PAGE_NAME, DASHBOARD_PAGE, EDITOR_PAGE, GOAL_PARADE_PAGE,
    KEY_MOMENT_PAGE_NAME,
    LIBRARY_PAGE_NAME, MATCH_HIGHLIGHT_PAGE, MATCHES_PAGE, PROCESSING_PAGE, ROLE_PAGE,
    RULES_PAGE, SOCIAL_PAGE, USERS_PAGE
} from "@/constants/globalConstants";


export interface TourStep {
    element?: string; //elemento che evidenzia lo step
    popover?: { // scheda che descrive la parte evidenziata
        title?: string;
        description?: string;
        side?: string;
        align?: string;
    }
    locationCheck?: () => void; //funzione asincrona per controllo della location (utile quando la route può non cambiare ma il contenuto si)
    route?: string; //rotta/location.pathname
    requireUserClick?: boolean; //insieme a makeRequiredStep, aiuta a far capire che è l'utente che deve cliccare sull' `${element}`
    skipMissingElement?: boolean; //se non trova l'element, skippare lo step
    disableActiveInteraction?: boolean; //disabilita l'interazione dell'utente
    waitForElement?: number; //numero di millisecondi da aspettare per trovare l'element
    checkListTextFields?: string[]; //stringhe che sono necessarie per quando sono necessari input (makeBoxRequired...)
    beforeNext?: () => void; //quando clicchi next, prima di andare allo step successivo, esegue la funzione
    onHighlightStarted?: () => void; //quando il contenuto viene evidenziato, c'è questa funzione

    [key: string]: any;
}

export let tourStepDict: Record<string, TourStep> = {
    //se sto in clips e voglio view all allora faccio questo check
    check_if_view_all: {
        locationCheck: async () => {

            // Se siamo già in view all, salta tutto
            if(document.querySelector('[data-tour="clips_page_show_less"]'))
                return;

            // Se siamo in Collection Clip, clicchiamo view all per andare alla view all generica della prima collection
            const viewall = await waitForElement('[data-tour="custom_collection_view_all_button"]', 10000);
            if (viewall instanceof HTMLElement) {
                viewall.click();
                await waitForElement('[data-tour="clips_page_show_less"]', 2000);
                // Piccola attesa per dare tempo a React di aggiornare il DOM
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }
        },
        route:'/' + CLIP_PAGE_NAME
    },
    //se sto in clips e non voglio stare in view all allora faccio questo check
    check_if_collection_clip: {
        locationCheck: async () => {

            //Se siamo già in Collection, salta tutto
            if(document.querySelector('[data-tour="custom_collection_view_all_button"]'))
                return;

            // Se siamo in View All, clicchiamo Show Less per andare alla collection page clips
            const showless = await waitForElement('[data-tour="clips_page_show_less"]', 2000);
            if (showless instanceof HTMLElement) {
                showless.click();
                await waitForElement('[data-tour="custom_collection_view_all_button"]')
                // Piccola attesa per dare tempo a React di aggiornare il DOM
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }
        },
        route:'/' + CLIP_PAGE_NAME
    },
    quick_actions_overview: {
        element: '[data-tour="quick_actions"]',
        popover: {title: "Global Header", description: "The current global header provides access to the platform's general features."}
    },
    quick_actions_box_overview: {
        element: '[data-tour="quick_actions_box"]',
        popover: {title: "Quick Actions", description: "Here you can select your preferred and quickest option for creating InBrief multimedia content."},
    },
    quick_actions_split_button_overview_and_click: makeRequiredClickStep({
        element: '[data-tour="split_button_quick_actions"]',
        popover: {title: 'Options', description: 'Click this arrow to open the list of the available contents and go on with the tour.'},
        requireUserClick: true
    }),
    quick_actions_list_buttons_overview: {
        element: '[data-tour="quick_actions_list"]',
        waitForElement: 500,
        skipMissingElement: true,
        popover: {title: 'Quick Actions', description: 'Here there\'s the list.', disableButtons: ['previous'],},
        beforeNext: async (element : any) => {
            const el = await waitForElement('[data-tour="split_button_quick_actions"]')

            if(el instanceof HTMLElement) {
                el.click()
            }
        }
    },
    ask_ai_overview: {
        element: '[data-tour="ask_ai"]',
        popover: {title: 'AI Help', description: "This button, on the other hand, lets you start a chat with InBrief's artificial intelligence to help you with your tasks"}
    },
    ask_ai_with_click: {
        element: '[data-tour="ask_ai"]',
        popover: {title: 'AI Help', description: "This button lets you start a chat with InBrief's artificial intelligence to help you with your tasks"},
        requireUserClick: true,
        beforeNext: async (element: any) => {
        const r = await waitForElement('[data-tour="total_chat_ai_ask"]', 2000).catch(() => null);
        if (r instanceof HTMLElement && r.clientWidth === 0) {
            // Fai la tua operazione prima di mostrare lo step se necessario
            if(element instanceof HTMLElement) {
                element.click()
            }
        }
}
    },
    total_chat_ai_ask_overview: {
        element: '[data-tour="total_chat_ai_ask"]',
        popover: {title: 'Chat', description: 'This is the full view of the chat with InBrief Assistant'}
    },
    ai_ask_drawer_button_overview: {
        element: '[data-tour="ai_ask_drawer_button"]',
        popover: {title: 'Drawer Button', description: 'You can hide the chat with the assistant at any time without losing the conversation.'}
    },
    ai_ask_message_list_overview: {
        element: '[data-tour="ai_ask_message_list"]',
        popover: {title: 'Messages', description: 'All messages are displayed here, starting from the beginning of the conversation initiated by the agent when the \“Ask AI\” button is clicked.'}
    },
    ask_ai_chat_input_overview: {
        element: '[data-tour="ask_ai_chat_input"]',
        popover: {title: 'Input Area', description: 'In this section you can request a task from InBrief Assistant.'}
    },
    keymoments_breadcrumbs_total_toggles_right_overview: {
        element: '[data-tour="breadcrumbs_total_toggles_right"]',
        disableActiveInteraction: true,
        popover: {title: 'General Collection Functions', description: 'Here you can create, filter and control general parameters of the showed collections.'},
        route: '/' + KEY_MOMENT_PAGE_NAME
    },
    keymoments_breadcrumbs_new_collection_button_overview: {
        element: '[data-tour="new_collection_button"]',
        disableActiveInteraction: true,
        popover: {title: 'Create a Collection', description: 'Here you can choose to create a new collection of the preferred type of content.'},
        route: '/' + KEY_MOMENT_PAGE_NAME
    },
    keymoments_breadcrumbs_dimension_changer_overview:{
        element: '[data-tour="dimension_changer_breadcrumbs"]',
        disableActiveInteraction: true,
        popover: {title: 'Dimension Selector', description: "You can choose the preferred dimension of cards contained in your collections."},
        route: '/' + KEY_MOMENT_PAGE_NAME
    },
    keymoments_breadcrumbs_trash_icon_overview: {
        element: '[data-tour="trash_icon_breadcrumbs"]',
        popover: {title: 'Delete Cards', description: "After selecting different cards of collections, you can delete them clicking this button."},
        disableActiveInteraction: true,
        route: '/' + KEY_MOMENT_PAGE_NAME
    },
    keymoments_library_breadcrumbs_trash_icon_overview: {
        element: '[data-tour="trash_icon_breadcrumbs"]',
        popover: {title: 'Delete Cards', description: "After selecting different cards of collections, you can delete them clicking this button."},
        disableActiveInteraction: true,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },
    keymoments_library_create_a_clip_with_selection_overview: {
        element: '[data-tour="create_a_clip_with_selection"]',
        popover: {title: 'Creation of Clips', description: 'After clicking on a single or different cards, you are able to create a clip with the selected ones.'},
        disableActiveInteraction: false,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },
    keymoments_library_filter_accordion_overview: {
        waitForElement: 1500,
        element: '[data-tour="filter_accordion_library"]',
        popover: {title: 'Filters', description: 'Here you can filter your cards by different properties'},
        disableActiveInteraction: false,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },
    keymoments_library_filter_accordion_field_search_overview: {
        element: '[data-tour="filter_accordion_field_search"]',
        popover: {title: 'Search', description: 'Here you can execute a global search on all available cards'},
        disableActiveInteraction: false,
        waitForElement: 500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },
    keymoments_library_filter_accordion_field_competition_overview: {
        element: '[data-tour="filter_accordion_field_competition"]',
        popover: {title: 'Competition', description: 'Here you can filter cards by their competition'},
        disableActiveInteraction: false,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },
    keymoments_library_filter_accordion_field_match_overview: {
        element: '[data-tour="filter_accordion_field_match"]',
        popover: {title: 'Match', description: 'Based on the eventual competition search, you can filter your cards by relevant and relative matches'},
        disableActiveInteraction: false,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },
    keymoments_library_filter_accordion_field_startSchedule_overview: {
        element: '[data-tour="filter_accordion_field_startSchedule"]',
        popover: {title: 'Match Date', description: 'You can set the date interval of the matches you want to filter'},
        disableActiveInteraction: false,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },
    keymoments_library_filter_accordion_field_key_moment_type_overview: {
        element: '[data-tour="filter_accordion_field_key_moment_type"]',
        popover: {title: 'Key Moment Types', description: 'You can set filters on the type of key moment to filter the cards based on their content'},
        disableActiveInteraction: false,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },
    keymoments_library_filter_accordion_field_player_search_overview: {
        element: '[data-tour="filter_accordion_field_player_search"]',
        popover: {title: 'Player', description: 'You can filter cards based on player referenced'},
        disableActiveInteraction: false,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },
    keymoments_library_filter_accordion_field_created_at_overview: {
        element: '[data-tour="filter_accordion_field_created_at"]',
        popover: {title: 'Production Date', description: 'Here you can filter cards based on the date they were created at'},
        disableActiveInteraction: false,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },
    keymoments_library_filter_accordion_field_tags_overview: {
        element: '[data-tour="filter_accordion_field_tags"]',
        popover: {title: 'Tags', description: 'Here you can filter cards based on their tags'},
        disableActiveInteraction: false,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },
    keymoments_library_filter_accordion_field_period_overview: {
        element: '[data-tour="filter_accordion_field_period"]',
        popover: {title: 'Period', description: 'Here you can filter your cards based on the desired eventual period of the game'},
        disableActiveInteraction: false,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },
    keymoments_library_filter_accordion_field_rating_overview: {
        element: '[data-tour="filter_accordion_field_rating"]',
        popover: {title: 'Rating', description: 'Here you can set the minimum rating for retrieved cards'},
        disableActiveInteraction: false,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },
    keymoments_library_filter_accordion_field_matchday_overview: {
        element: '[data-tour="filter_accordion_field_matchday"]',
        popover: {title: 'Matchday', description: 'Here you can filter cards based on their matchday'},
        disableActiveInteraction: false,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },
    keymoments_library_filter_accordion_field_selected_overview: {
        element: '[data-tour="filter_accordion_field_selection"]',
        popover: {title: 'Selected Cards', description: 'Here you can see just the cards you have selected'},
        disableActiveInteraction: false,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },


    keymoments_breadcrumbs_filter_drawer_overview: {
        element: '[data-tour="filters_drawer"]',
        popover: {title: 'Filter Collections', description: "Here you can open the Filter Drawer to set filters and visualize your preferred collections."},
        route: '/' + KEY_MOMENT_PAGE_NAME
    },
    keymoments_custom_collection_overview: {
        element: '[data-tour="custom_collection"]',
        disableActiveInteraction: true,
        popover: {title: 'Collection', description: "Each collection is a list of different cards which share the same parameters.", side: 'bottom', align:'center'},
        route: '/' + KEY_MOMENT_PAGE_NAME
    },
    keymoments_custom_collection_automatic_label_overview: {
        element: '[data-tour="automatic_label_collection"]',
        disableActiveInteraction: true,
        popover: {title: 'Automatic Collection', description: 'This label determines if a collection was created automatically.'},
        route: '/' + KEY_MOMENT_PAGE_NAME
    },
    keymoments_custom_collection_actions_right_overview: {
        element: '[data-tour="custom_collection_actions_right"]',
        disableActiveInteraction: true,
        popover: {title: 'Collection Actions', description: "There are different actions you can execute on a single collection"},
        route: '/' + KEY_MOMENT_PAGE_NAME
    },
    keymoments_custom_collection_actions_pin_overview: {
        element: '[data-tour="custom_collection_actions_pin"]',
        disableActiveInteraction: true,
        popover: {title: 'Pin Collection', description: 'The pin highlights or downplays a collection, allowing it to be viewed on the first or last pages'},
        route: '/' + KEY_MOMENT_PAGE_NAME
    },
    keymoments_custom_collection_actions_hidden_overview: {
        element: '[data-tour="custom_collection_actions_hidden"]',
        disableActiveInteraction: true,
        popover: {title: 'Hidden', description: 'Click this icon to hide or view another time the current collection'},
        route: '/' + KEY_MOMENT_PAGE_NAME
    },
    keymoments_custom_collection_actions_edit_overview: {
        element: '[data-tour="custom_collection_actions_edit"]',
        disableActiveInteraction: true,
        popover: {title: 'Edit', description: 'To edit the current collection'},
        route: '/' + KEY_MOMENT_PAGE_NAME
    },
    keymoments_custom_collection_actions_delete_overview: {
        element: '[data-tour="custom_collection_actions_delete"]',
        disableActiveInteraction: true,
        popover: {title: 'Delete', description: 'To delete the current collection'},
        route: '/' + KEY_MOMENT_PAGE_NAME
    },
    keymoments_custom_collection_first_card_available_overview: {
        element: '[data-tour="single_card"]',
        disableActiveInteraction: true,
        popover: {title: 'Card', description: 'Each card describes a key moment or clip content. Clicking on it means select it, while pointing it shows you different actions, like Edit, Info, Play, ...'},
        beforeNext: async (element : any) => {

            const isAlreadyFlipped = !!document.querySelector('[data-tour="hidden_element_for_tour"]');

            // Se è già flippata, non facciamo nulla e andiamo avanti
            if (isAlreadyFlipped) {
                return;
            }

                if (!(element instanceof HTMLElement)) return;

                // Invia gli eventi di hover a TUTTI gli elementi figli per raggiungere cardRef in WipeCard
                const targets = [element, ...Array.from(element.querySelectorAll('*'))];
                targets.forEach((target) => {
                    target.dispatchEvent(new MouseEvent('mouseenter', {bubbles: true, cancelable: true}));
                    target.dispatchEvent(new MouseEvent('mouseover', {bubbles: true, cancelable: true}));
                    target.dispatchEvent(new PointerEvent('pointerenter', {bubbles: true, cancelable: true}));
                });

                // Attendi che React aggiorni lo stato e imposti backMounted = true
                await new Promise((resolve) => setTimeout(resolve, 150));


                // 3. Recupera il contenitore del pulsante metadata
                const buttonForMetadata = await waitForElement('[data-tour="single_card_metadata_button"]');

                if (buttonForMetadata instanceof HTMLElement) {
                    // 4. Seleziona ed esegui il click direttamente sul <Chip> interno (dove risiede l'onClick di React)
                    const chipTarget =
                        buttonForMetadata.querySelector('.MuiChip-root') ||
                        buttonForMetadata.querySelector('div, span, button') ||
                        buttonForMetadata;

                    (chipTarget as HTMLElement).click();
                }

                // 5. Mantiene attivo l'hover per evitare che scheduleDespawn smonti il retro durante la transizione
                targets.forEach((target) => {
                    target.dispatchEvent(new MouseEvent('mouseenter', {bubbles: true, cancelable: true}));
                });
        },
        route: '/' + KEY_MOMENT_PAGE_NAME
    },
    keymoments_custom_collection_first_card_available_metadata_overview: {
        element: '[data-tour="single_card"]',
        disableActiveInteraction: true,
        popover: {title: 'Metadata', description: 'Clicking on any point of the card flips it and lets you see important data regarding the current content', disableButtons: ['previous']},
        route: '/' + KEY_MOMENT_PAGE_NAME
    },
    keymoments_library_first_card_available_metadata_overview: {
        element: '[data-tour="single_card"]',
        waitForElement: 100,
        disableActiveInteraction: true,
        popover: {title: 'Card', description: 'Each card describes a key moment based on your current filters. Clicking on any point of the card will flip it and see important data regarding the current content'},
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    },
    keymoments_custom_collection_open_in_library_button_overview_and_click: makeRequiredClickStep({
        element: '[data-tour="custom_collection_open_in_library_button"]',
        popover: {title: 'Library Button', description: 'Clicking this button will show you the key moment library.'},
        requireUserClick: true,
        route: '/' + KEY_MOMENT_PAGE_NAME
    }),
    clips_breadcrumbs_total_toggles_right_overview: {
        element: '[data-tour="breadcrumbs_total_toggles_right"]',
        popover: {title: 'General Collection Functions', description: 'Here you can create, filter and control general parameters of the showed collections.'},
        route: '/' + CLIP_PAGE_NAME
    },
    clips_breadcrumbs_new_collection_button_overview: {
        element: '[data-tour="new_collection_button"]',
        popover: {title: 'Create a Collection', description: 'Here you can choose to create a new collection of the preferred type of content.'},
        route: '/' + CLIP_PAGE_NAME
    },
    clips_breadcrumbs_dimension_changer_overview:{
        element: '[data-tour="dimension_changer_breadcrumbs"]',
        popover: {title: 'Dimension Selector', description: "You can choose the preferred dimension of cards contained in your collections."},
        route: '/' + CLIP_PAGE_NAME
    },
    clips_breadcrumbs_trash_icon_overview: {
        element: '[data-tour="trash_icon_breadcrumbs"]',
        popover: {title: 'Delete Cards', description: "After selected different cards of collections, you can delete them clicking this button."},
        route: '/' + CLIP_PAGE_NAME
    },
    clips_breadcrumbs_filter_drawer_overview: {
        element: '[data-tour="filters_drawer"]',
        popover: {title: 'Filter Collections', description: "Here you can open the Filter Drawer to set filters and visualize your preferred collections."},
        route: '/' + CLIP_PAGE_NAME
    },
    clips_breadcrumbs_filter_drawer_inside_collection_overview: {
        element: '[data-tour="filters_drawer"]',
        popover: {title: 'Filter Clips', description: 'Here you can open the Filter Drawer to set filters and visualize your preferred clips inside the current collection.'},
        requireUserClick: true,
        route: '/' + CLIP_PAGE_NAME,
        beforeNext: async (element : any) => {
            if(element instanceof HTMLElement)
                element.click()
        }
    },
    clips_breadcrumbs_filter_drawer_inside_collection_overview_all: {
        element: '[data-tour="filter_drawer_overview"]',
        popover: {title: 'Filters', description: 'Here you can see all the available filters for retrieving the preferred clips based on your parameters.'},
        route: '/' + CLIP_PAGE_NAME
    },
    clips_custom_collection_overview: {
        element: '[data-tour="custom_collection"]',
        popover: {title: 'Collection', description: "Each collection is a list of different cards which share the same parameters.", side: 'bottom', align:'center'},
        route: '/' + CLIP_PAGE_NAME
    },
    clips_custom_collection_view_all_button_overview_and_click: {
        element: '[data-tour="custom_collection_view_all_button"]',
        popover: {title: 'View All Clips', description: 'Clicking this button you\'ll see all the clips inside the current collection.'},
        beforeNext: async (element : any) => {
            if(element instanceof HTMLElement) {
                element.click()
                await waitForElement('[data-tour="single_card_overview"]', 1000)
                await new Promise((resolve) => setTimeout(resolve, 300));
            }
        },
        route: '/' + CLIP_PAGE_NAME
    },
    clips_custom_collection_actions_right_overview: {
        element: '[data-tour="custom_collection_actions_right"]',
        popover: {title: 'Collection Actions', description: "There are different actions you can execute on a single collection"},
        route: '/' + CLIP_PAGE_NAME
    },
    clips_custom_collection_actions_pin_overview: {
        element: '[data-tour="custom_collection_actions_pin"]',
        popover: {title: 'Pin Collection', description: 'The pin highlights or downplays a collection, allowing it to be viewed on the first or last pages.'},
        route: '/' + CLIP_PAGE_NAME
    },
    clips_custom_collection_actions_hidden_overview: {
        element: '[data-tour="custom_collection_actions_hidden"]',
        popover: {title: 'Hidden', description: 'Click this icon to hide or view another time the current collection.'},
        route: '/' + CLIP_PAGE_NAME
    },
    clips_custom_collection_actions_edit_overview: {
        element: '[data-tour="custom_collection_actions_edit"]',
        popover: {title: 'Edit', description: 'To edit the current collection.'},
        route: '/' + CLIP_PAGE_NAME
    },
    clips_custom_collection_actions_delete_overview: {
        element: '[data-tour="custom_collection_actions_delete"]',
        popover: {title: 'Delete', description: 'To delete the current collection'},
        route: '/' + CLIP_PAGE_NAME
    },
    clips_view_all_card_overview: {
        element: '[data-tour="single_card_overview"]',
        waitForElement: 2000,
        popover: {title: 'Card', description: 'Each card describes a key moment or clip content. Clicking on it means select it, while pointing it shows you different actions, like Edit, Info, Play, ...'},
    },
    clips_custom_collection_first_card_available_overview: {
        element: '[data-tour="single_card"]',
        popover: {title: 'Card', description: 'Each card describes a key moment or clip content. Clicking on it means select it, while pointing it shows you different actions, like Edit, Info, Play, ...'},
        beforeNext: async (element : any) => {

            const isAlreadyFlipped = !!document.querySelector('[data-tour="hidden_element_for_tour"]');

            // Se è già flippata, non facciamo nulla e andiamo avanti
            if (isAlreadyFlipped) {
                return;
            }

            if (!(element instanceof HTMLElement)) return;

            //Purtroppo qui ho dovuto usare il maestro perché era impossibile da solo con la nuova ottimizzazione
            // 1. Invia gli eventi di hover a TUTTI gli elementi figli per raggiungere cardRef in WipeCard
            const targets = [element, ...Array.from(element.querySelectorAll('*'))];
            targets.forEach((target) => {
                target.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true, cancelable: true }));
                target.dispatchEvent(new MouseEvent('mouseover', { bubbles: true, cancelable: true }));
                target.dispatchEvent(new PointerEvent('pointerenter', { bubbles: true, cancelable: true }));
            });

            // 2. Attendi che React aggiorni lo stato e imposti backMounted = true
            await new Promise((resolve) => setTimeout(resolve, 150));

            // 3. Recupera il contenitore del pulsante metadata
            const buttonForMetadata = await waitForElement('[data-tour="single_card_metadata_button"]');

            if (buttonForMetadata instanceof HTMLElement) {
                // 4. Seleziona ed esegui il click direttamente sul <Chip> interno (dove risiede l'onClick di React)
                const chipTarget =
                    buttonForMetadata.querySelector('.MuiChip-root') ||
                    buttonForMetadata.querySelector('div, span, button') ||
                    buttonForMetadata;

                (chipTarget as HTMLElement).click();
            }

            // 5. Mantiene attivo l'hover per evitare che scheduleDespawn smonti il retro durante la transizione
            targets.forEach((target) => {
                target.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true, cancelable: true }));
            });
        },
        route: '/' + CLIP_PAGE_NAME
    },
    clips_custom_collection_first_card_available_metadata_overview: {
        element: '[data-tour="single_card"]',
        popover: {title: 'Metadata', description: 'Clicking on any point of the card you can flip it and see important data regarding the current content', disableButtons: ['previous']},
        route: '/' + CLIP_PAGE_NAME
    },
    //RULES PAGE
    rules_create_a_rule_button_overview: {
        element: '[data-tour="rule_create_new_button"]',
        popover: {title: 'Creation of Rules', description: 'Each rule describes a trigger to create new automated content thanks to InBrief Agents.'},
        route: '/' + RULES_PAGE
    },
    rules_create_a_rule_button_click: {
        element: '[data-tour="rule_create_new_button"]',
        waitForElement: 200,
        popover: {title: 'Creation of Rules', description: 'Each rule describes a trigger to create new automated content thanks to InBrief Agents.'},
        route: '/' + AUTOMATION_FORGE
    },

    rules_cretion_automation_name_write_textfield: makeRequiredInputStep({
        waitForElement: 1000,
        element: '[data-tour="rules_automation_name_textfield"]',
        popover: {title: 'Rule Name', description: 'Write here the name of the rule.'},
        route: '/' + AUTOMATION_FORGE
    }),
    rules_creation_automation_description_write_textfield: makeRequiredInputStep({
        element: '[data-tour="rules_automation_description_textfield"]',
        popover: {title: 'Rule Description', description: 'Here you can describe the focus and topic of your current trigger'},
        route: '/' + AUTOMATION_FORGE
    }),
    rules_creation_automation_category_custom_autocomplete: makeRequiredInputStep({
        element: '[data-tour="rule_creation_automation_category"]',
        popover: {title: 'Rule Category', description: 'Here you can choose between Time-Based rule or, instead, based on calendar, platform Agents or events.', side:'left'},
        route: '/' + AUTOMATION_FORGE
    }),
    rules_creation_trigger_type_custom_autocomplete: makeRequiredInputStep({
       element: '[data-tour="rule_creation_trigger_type_field"]',
       popover: {title: 'Trigger Type', description: 'Based on your rule\'s category, you can choose a specific trigger type based on your needs.', side:'left'},
        route: '/' + AUTOMATION_FORGE
    }),
    rules_creation_content_type_custom_autocomplete: makeRequiredInputStep({
        element: '[data-tour="rule_creation_content_type_textfield"]',
        popover: {title: 'Content Type', description: 'You can choose the content type result of your rule', side: 'left'},
        route: '/' + AUTOMATION_FORGE
    }),

    rules_creation_delay_custom_autocomplete: {
        element: '[data-tour="rules_time_params_box"]',
        popover: {title: 'Delay', description: 'You can choose the amount of seconds after the activation of conditions you\'ll determine below for the creation of the desired content.'},
        waitForElement: 500,
        skipMissingElement: true,
        route: '/' + AUTOMATION_FORGE
    },


    rules_creation_activation_condition_box: makeBoxRequiredInputStep({
        waitForElement: 500,
        element: '[data-tour="rules_activation_conditions_total_box"]',
        popover: {title: 'Activation Conditions', description: 'Here you must insert the conditions that, put together and true, will activate the rule to create the requested content.', side:'top'},
        skipMissingElement: true,
        padding: 15,
        checkListTextFields: ['[data-tour="rules_match_search_full"]', '[data-tour="rules_matchday_search_full"]', '[data-tour="rules_team_search_full"]', '[data-tour="rules_custom_autocomplete_condition_form"]', '[data-tour="rules_textfield_condition_form_full"]', '[data-tour="rules_datetime_picker_condition_form"]', '[data-tour="rules_chip_textfield_condition_form"]', '[data-tour="rules_multichip_textfield_condition_form"]'],
        route: '/' + AUTOMATION_FORGE
    }),
    rules_creation_filters_box: {
        waitForElement: 500,
        element: '[data-tour="rules_filters_total_box"]',
        popover: {title: 'Filters', description: 'Here you can filter the events that can result in the requested content', side: 'top'},
        skipMissingElement: true,
        padding: 15,
        route: '/' + AUTOMATION_FORGE
    },
    rules_creation_filter_conditions_box: {
        waitForElement: 500,
        element: '[data-tour="rules_time_conditions_total_box"]',
        popover: {title: 'Time Conditions', description: 'Here you must insert the activation conditions related to time-based rules', side:'top'},
        skipMissingElement: true,
        padding: 15,
        route: '/' + AUTOMATION_FORGE
    },
    rules_content_aggregation_box_overview: {
        waitForElement: 500,
        element: '[data-tour="rules_content_aggregation_box"]',
        popover: {title: 'Creation Parameters', description: 'Through this section, you\'ll be able to set different parameters useful for the requested contents'},
        padding: 15,
        route: '/' + AUTOMATION_FORGE
    },
    rules_content_aggregation_actions_section_overview: {
        element: '[data-tour="rules_content_aggregation_actions_section"]',
        popover: {title: 'Actions', description: 'These are the different actions you can execute on the current rule. Choose the preferred one.',},
        waitForElement: 500,
        skipMissingElement: true,
        padding: 15,
        route: '/' + AUTOMATION_FORGE
    },
    rules_action_parameter_content_textfield: makeRequiredInputStep({
        element: '[data-tour="rules_action_parameters_textfield"]',
        popover: {title: 'Action Parameters', description: 'Put the requested info to complete the settings of the rule'},
        padding: 15,
        skipMissingElement: true,
        route: '/' + AUTOMATION_FORGE
    }),
    rules_social_publish_section_overview_clickable: {
        element: '[data-tour="rules_social_publish_section"]',
        popover: {title: 'Social Parameters', description: 'This section helps you setting social parameters for publishing your desired contents'},
        route: '/' + AUTOMATION_FORGE
    },

    rules_event_based_table_overview: {
        element: '[data-tour="rule_event_based_table"]',
        popover: {title: 'Event-Based Rules', description: 'These are rules that triggers the creation of contents based on specified events'},
        route: '/' + RULES_PAGE
    },
    rules_trigger_event_based_rule_header_overview: {
        element: '.MuiDataGrid-columnHeader[data-field="trigger"]',
        popover: {title: 'Trigger type', description: 'Here you can see the trigger type of each rule, based on speicifc arguments of them and events parameters.'},
        route: '/' + RULES_PAGE
    },
    rules_enabled_event_based_rule_header_overview: {
        element: '.MuiDataGrid-columnHeader[data-field="enabled"]',
        popover: {title: 'Enabled Status', description: 'Here you can enable or disable a trigger'},
        route: '/' + RULES_PAGE
    },
    rules_event_based_edit_button_overview: {
        element: '[data-tour="rule_edit_button_overview"]',
        popover: {title: 'Edit Button', description: 'Here you can edit an existent rule'},
        route: '/' + RULES_PAGE
    },
    rules_event_based_delete_button_overview: {
        element: '[data-tour="rule_delete_button_overview"]',
        popover: {title: 'Delete Button', description: 'Here you can delete an existent rule'},
        route: '/' + RULES_PAGE
    },
    rules_time_based_table_overview: {
        element: '[data-tour="rule_time_based_table"]',
        popover: {title: 'Time-Based Rules', description: "These are rules that trigger the creation of scheduled contents based on specified events"},
        route: '/' + RULES_PAGE
    },

    monitor_button_group_overview_and_click: {
        element: '[data-tour="monitor_button_group_view_box"]',
        popover: {title: 'Views', description: 'Here you can choose the preferred view of your Agents, i.e. table view and card view'},
        waitForElement: 200,
        beforeNext: async (element: any) => {
            const el = await waitForElement('[data-tour="monitor_button_group_chart"]')

            if(el instanceof HTMLElement) {
                el.click()
            }
        },
        route: '/' + AGENT_PAGE
    },
    monitor_chart_overview: {
        element: '[data-tour="monitor_chart_jobs"]',
        popover: {title: 'Table', description: 'Here you can see and order your Agents by their properties, like status, date, type and progress', side:'top'},
        waitForElement: 1000,
        beforeNext: async (element: any) => {
            const el = await waitForElement('[data-tour="monitor_button_group_carousel"]')
            if(el instanceof HTMLElement) {
                el.click()
            }
        },
        route: '/' + AGENT_PAGE
    },
    monitor_card_overview: {
        element: '[data-tour="monitor_rendering_agent_card"]',
        popover: {title: 'Card', description: 'Each agent can be visualized as a single card with their important metadata', disableButtons: ['previous']},
        skipMissingElement: true,
        waitForElement: 300,
        route: '/' + AGENT_PAGE
    },
    monitor_sorter_cards_overview: {
        element: '[data-tour="monitor_sorter_cards"]',
        popover: {title: 'Sorting Fields', description: 'Each agent can be sorted through the values of these dropdown fields', side:'right'},
        disableActiveInteraction: false,
        route: '/' + AGENT_PAGE
    },
    monitor_group_by_overview: {
        element: '[data-tour="monitor_group_by"]',
        popover: {title: 'Group By Property', description: 'Here you can group by your agents by their properties', side:'left'},
        disableActiveInteraction: false,
        route: '/' + AGENT_PAGE
    },

    social_single_overview: {
        element: '[data-tour="social_wide_box"]',
        popover: {title: 'Social Platform', description: 'You can manage your social accounts for each social platform you have at your disposal.'},
        route: '/' + SOCIAL_PAGE,
        disableActiveInteraction: true
    },
    social_add_account_overview: {
        element: '[data-tour="social_add_account_overview"]',
        popover: {title: 'Add Account', description: 'You can add your account through this button'},
        disableActiveInteraction: true,
        route: '/' + SOCIAL_PAGE
    },
    social_retrieve_info: {
        element: '[data-tour="social_retrieval_dev"]',
        popover: {title: 'Stuck?', description: 'If you encounter some problems linking your account to InBrief, check the related documentation'},
        disableActiveInteraction: true,
        skipMissingElement: true,
        route: '/' + SOCIAL_PAGE
    },
    social_instagram_wide_box_overview: {
        element: '[data-tour="social_instagram_wide_box"]',
        popover: {title: 'Instagram Account', description: 'Let\'s link your Instagram Account to InBrief'},
        disableActiveInteraction: true,
        route: '/' + SOCIAL_PAGE
    },
    social_instagram_add_account_and_click: makeRequiredClickStep({
        element: '[data-tour="social_instagram_add_account"]',
        popover: {title: 'Linking your Account', description: 'Let\'s link your Instagram Account to InBrief clicking on Next'},
        disableActiveInteraction: true,
        requireUserClick: true,
        route: '/' + SOCIAL_PAGE
    }),

    dashboard_filters_box_overview: makeBoxRequiredInputStep({
        element: '[data-tour="dashboard_filters_box"]',
        popover: {title: 'Filters', description: 'You can see different statistics based on your time, content and event filters'},
        route: '/' + DASHBOARD_PAGE
    }),
    dashboard_kpis_overview: {
        element: '[data-tour="dashboard_kpis"]',
        popover: {title: 'KPIs', description: 'Here there are the summarized results of your contents based on your filters'},
        route: '/' + DASHBOARD_PAGE
    },
    dashboard_daily_stats_overview: {
        element: '[data-tour="dashboard_daily_stats_content_type_card"]',
        popover: {title: 'Daily Statistics', description: 'Here there are different daily stats grouped by content type.'},
        disableActiveInteraction: false,
        route: '/' + DASHBOARD_PAGE
    },
    dashboard_social_media_distribution: {
        element: '[data-tour="dashboard_social_media_distribution"]',
        popover: {title: 'Social Distributions', description: 'If you have published your contents through InBrief socials, you\'ll see the engagement right here'},
        disableActiveInteraction: false,
        route: '/' + DASHBOARD_PAGE
    },
    dashboard_match_engagement_overview: {
        element: '[data-tour="dashboard_match_engagement"]',
        popover: {title: 'Match Engagement', description: 'Based on your matches, you\'ll see their statistics about social engagement'},
        disableActiveInteraction: false,
        route: '/' + DASHBOARD_PAGE
    },
    dashboard_match_engagement_ranking_overview: {
        element: '[data-tour="dashboard_match_engagement_ranking"]',
        popover: {title: 'Match Engagement Ranking Overview', description: 'Your matches will be ranked based on match engagement on your linked social accounts'},
        disableActiveInteraction: false,
        route: '/' + DASHBOARD_PAGE
    },


    ingestion_overview_card: {
        element: '[data-tour="ingestion_overview_card"]',
        popover: {title: 'Ingestion Agent', description: 'Here there are the total details and parameters for creating your Ingestion Agent'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE + '/' + PROCESSING_PAGE +'/'
    },
    ingestion_video_source_overview: {
        element: '[data-tour="ingestion_video_source_section"]',
        popover: {title: 'Video Source', description: 'Here you can choose your video source for the retrieval of your desired contents'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE + '/' + PROCESSING_PAGE+'/'
    },
    ingestion_tracks_overview: {
        element: '[data-tour="ingestion_tracks_box"]',
        popover: {title: 'Tracks', description: 'Here you can manage different video and audio tracks of your desired content, indexing based on the combination you want'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE + '/' + PROCESSING_PAGE+'/'
    },
    ingestion_agent_settings_overview: {
        element: '[data-tour="ingestion_agent_settings_box"]',
        popover: {title: 'Agent Settings', description: 'Here you can set the activation time of the agent and eventual other settings'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE + '/' + PROCESSING_PAGE+'/'
    },
    ingestion_schedule_agent_button: {
        element: '[data-tour="ingestion_schedule_agent_button"]',
        popover: {title: 'Schedule Agent', description: 'After all the settings related to the content, the creation date and tracks of your agent, you can now schedule it by pressing this button'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE + '/' + PROCESSING_PAGE+'/'
    },

    extraction_agent_card_overview: {
        element: '[data-tour="extraction_agent_card"]',
        popover: {title: 'Extraction Agent', description: 'Here you can set parameters and creation date of your extraction agent'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE + '/' + PROCESSING_PAGE+'/'
    },
    extraction_agent_main_key_moments_overview_clickable: {
        element: '[data-tour="extraction_agent_main_key_moments"]',
        popover: {title: 'Main Key Moments', description: 'Here you can enable or disable different main key moments from your extracted contents with start and end time parameters'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE + '/' + PROCESSING_PAGE+'/'
    },
    extraction_agent_skills_overview_clickable: {
        element: '[data-tour="extraction_agent_skills_box"]',
        popover: {title: 'Skills', description: 'Here you can enable or disable different skills with start/end time parameters'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE + '/' + PROCESSING_PAGE+'/'
    },
    extraction_agent_settings_overview_clickable: {
        element: '[data-tour="extraction_agent_settings_box"]',
        popover: {title: 'Agent Settings', description: 'Here you can set different time and content parameters for the creation of your Extraction Agent. Also you can manage the materializations and overwriting of cuts'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE + '/' + PROCESSING_PAGE+'/'
    },
    extraction_agent_button_clickable_schedule: {
        element: '[data-tour="extraction_agent_button"]',
        popover: {title: 'Schedule Extraction', description: 'After creating your Ingestion Agent and managing the settings of your extraction agent, you can finally schedule it'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE + '/' + PROCESSING_PAGE+'/'
    },

    processing_page_card_event_overview: {
        element: '[data-tour="processing_page_event_header_card"]',
        popover: {title: 'Processing Event', description: 'Here the important information about the current event'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE + '/' + PROCESSING_PAGE + '/'
    },
    processing_page_key_moment_overview: {
        element: '[data-tour="processing_auto_collection_section"]',
        popover: {title: 'Key Moments', description: 'Here there are key moments related to the event'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE + '/' + PROCESSING_PAGE +'/'
    },

    matches_carousel_overview: {
        element: '[data-tour="ingestion_job_carousel_box"]',
        popover: {title: 'Injection Jobs Carousel', description: 'Here you can see the ingestion jobs currently active in your platform'},
        disableActiveInteraction: true,
        skipMissingElement: true,
        route: '/' + MATCHES_PAGE
    },
    matches_carousel_button_pin: {
        element: '[data-tour="pin_carousel_button"]',
        popover: {title: 'Pin Carousel', description: 'Eventually you can pin your ingestion jobs carousel clicking on this button'},
        skipMissingElement: true,
        disableActiveInteraction: false,
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_matchboard_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                // Piccola attesa per dare tempo a React di aggiornare il DOM
                await new Promise((resolve) => setTimeout(resolve, 400));
            }
        },
        route: '/' + MATCHES_PAGE
    },
    matches_card_matchboard_calendar_overview: {
        element: '[data-tour="matches_matchboard_calendar_card"]',
        popover: {title: 'Detailed Matches Overview', description: 'Here you can see all the matches in the platform through two different views, matchboard (for matchdays) and calendar (for time parameters)'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE
    },
    matches_card_matchboard_calendar_filters_and_params: {
        element: '[data-tour="matches_parameters_upside"]',
        popover: {title: 'Parameters and Filters', description: 'In particular, here you can choose the competition, search filters and, obviously, the desired view'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE
    },
    matches_view_toggle_button_group_and_click: {
        element: '[data-tour="ingestion_button_view_group"]',
        popover: {title: 'Match Views', description: 'You can choose the view of matches based on desired parameters, events or time'},
        beforeNext: async (element: any) => {
            const but = await waitForElement('[data-tour="ingestion_matchboard_button"]')
            but.click()
        },
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE
    },
    matches_total_view_matchboard_overview: {
        element: '[data-tour="matchboard_total_box"]',
        popover: {title: 'Matchdays', description: 'Here there are all the available matchdays in platform.'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE
    },
    matches_no_matchday_associated_box_overview_and_click: {
        element: '[data-tour="matchday_single_box"][data-nomatchday="true"]',
        popover: {title: 'No Matchday', description: 'Here there all the matches without matchday associated'},
        disableActiveInteraction: true,
        beforeNext: async (element: any) => {
            const but = await waitForElement('[data-tour="matchboard_toggle_expand_icon"][data-nomatchday="true"]')
            but.click()
        },
        route: '/' + MATCHES_PAGE
    },
    matches_expanded_matchday: {
        element: '[data-tour="matches_expanded_matchday"]',
        popover: {title: 'Matches', description: 'Here you can see different matches of the associated matchday with all of their summarized informations', disableButtons: ['previous']},
        disableActiveInteraction: true,
        beforeNext: async (element: any) => {
            const but = await waitForElement('[data-tour="matchboard_toggle_expand_icon"][data-nomatchday="true"]')
            but.click()
        },
        route: '/' + MATCHES_PAGE
    },
    matches_second_matchday_single_box: {
        element: '[data-tour="matchday_single_box"][data-nomatchday="false"]',
        popover: {title: 'Matchday', description: 'You can modify each matchday and it will contain related matches based on time and event parameters'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE
    },
    matches_date_associated_matchday: {
        element: '[data-tour="matchday_associated_time"][data-nomatchday="false"]',
        popover: {title: 'Date Interval', description: 'Here there\'s the time interval of the current matchday'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE
    },
    matches_matchday_actions_overview: {
        element: '[data-tour="matches_actions_matchday_box"][data-nomatchday="false"]',
        popover: {title: 'Matchday Actions', description: 'Here you can drag and drop, delete or expand your matchday'},
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE
    },
    matches_calendar_view_button_overview_and_click: makeRequiredClickStep({
        element: '[data-tour="ingestion_calendar_button"]',
        popover: {title: 'Calendar View', description: 'Calendar View lets you see your matches grouped by different time interval'},
        disableActiveInteraction: false,
        requireUserClick: true,
        route: '/' + MATCHES_PAGE
    }),
    matches_toolbar_calendar_view_overview: {
        element: '[data-tour="matches_calendar_toolbar"]',
        waitForElement: 500,
        popover: {title: 'Time Toolbar', description: 'Here you can manage and move your time interval as you want', disableButtons: ['previous']},
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_calendar_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
            }
        },
        disableActiveInteraction: true,
        route: '/' + MATCHES_PAGE
    },
    matches_buttongroup_view_calendar: {
        element: '[data-tour="matches_calendar_toggle_buttongroup"]',
        popover: {title: 'Calendar Views', description: 'Here you can choose the time interval and different views'},
        disableActiveInteraction: true,
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_calendar_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
            }
        },
        route: '/' + MATCHES_PAGE
    },
    matches_buttongroup_view_calendar_table_overview_and_click: makeRequiredClickStep({
        element: '[data-tour="matches_calendar_toggle_table"]',
        popover: {title: 'Table View', description: 'Let\'s see the Table'},
        disableActiveInteraction: false,
        requireUserClick: true,
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_calendar_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
            }
        },
        waitForElement: 1500,
        route: '/' + MATCHES_PAGE
    }),
    matches_table_calendar_view_total: {
        element: '[data-tour="matches_dense_table_total_view"]',
        popover: {title: 'Table', description: 'Here you can see and manage ingestion and extraction agents of related matches'},
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_calendar_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
                const agenda = await waitForElement('[data-tour="matches_calendar_toggle_table"]', 2000)
                if(agenda instanceof HTMLElement) {
                    agenda.click()
                }
            }
        },
        waitForElement: 1500,
        route: '/' + MATCHES_PAGE
    },
    matches_table_calendar_view_ingestion_header_column: {
        element: '[data-tour="matches_table_field_ingestion"]',
        popover: {title: 'Ingestion Agent', description: 'Here you can see the status of the eventual ingestion job related to the current event'},
        disableActiveInteraction: true,
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_calendar_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
                const table = await waitForElement('[data-tour="matches_calendar_toggle_table"]', 2000)
                if(table instanceof HTMLElement) {
                    table.click()
                }
            }
        },
        waitForElement: 500,
        route: '/' + MATCHES_PAGE
    },
    matches_table_calendar_view_extraction_column_header: {
        element: '[data-tour="matches_table_field_extraction"]',
        popover: {title: 'Extraction Agent', description: 'Here you can see the status of the eventual extraction job related to the current event with related key moments'},
        disableActiveInteraction: true,
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_calendar_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
                const table = await waitForElement('[data-tour="matches_calendar_toggle_table"]', 2000)
                if(table instanceof HTMLElement) {
                    table.click()
                }
            }
        },
        waitForElement: 500,
        route: '/' + MATCHES_PAGE
    },
    matches_table_calendar_view_operation_column_header: {
        element: '[data-tour="matches_table_field_operations"]',
        popover: {title: 'Operations', description: 'Here you can view and manage the agents related to the current event'},
        disableActiveInteraction: true,
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_calendar_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
                const table = await waitForElement('[data-tour="matches_calendar_toggle_table"]', 2000)
                if(table instanceof HTMLElement) {
                    table.click()
                }
            }
        },
        waitForElement: 500,
        route: '/' + MATCHES_PAGE
    },
    matches_buttongroup_view_calendar_agenda_overview_and_click: makeRequiredClickStep({
        element: '[data-tour="matches_calendar_toggle_agenda"]',
        popover: {title: 'Agenda View', description: 'Let\'s see the Agenda', disableButtons: ['previous']},
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_calendar_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
            }
        },
        requireUserClick: true,
        waitForElement: 1500,
        route: '/' + MATCHES_PAGE
    }),
    matches_calendar_box_overview_agenda: {
        element: '[data-tour="matches_calendar_view_box"]',
        popover: {title: 'Agenda', description: 'For each event you can see the time interval and eventually click on it to go to the editor of related contents', disableButtons: ['previous']},
        disableActiveInteraction: true,
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_calendar_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
                const table = await waitForElement('[data-tour="matches_calendar_toggle_agenda"]', 2000)
                if(table instanceof HTMLElement) {
                    table.click()
                }
            }
        },
        waitForElement: 500,
        route: '/' + MATCHES_PAGE
    },
    matches_buttongroup_view_calendar_month_overview_and_click: makeRequiredClickStep({
        element: '[data-tour="matches_calendar_toggle_month"]',
        popover: {title: 'Month View', description: 'Let\'s see the month view', disableButtons: ['previous']},
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_calendar_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
            }
        },
        requireUserClick: true,
        waitForElement: 1500,
        route: '/' + MATCHES_PAGE
    }),
    matches_calendar_box_overview_month: {
        element: '[data-tour="matches_calendar_view_box"]',
        popover: {title: 'Month', description: 'For each event you can see the time interval, date and eventually, clicking on it, you\'ll navigate the editor of related contents', disableButtons: ['previous']},
        disableActiveInteraction: true,
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_calendar_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
                const table = await waitForElement('[data-tour="matches_calendar_toggle_month"]', 2000)
                if(table instanceof HTMLElement) {
                    table.click()
                }
            }
        },
        waitForElement: 500,
        route: '/' + MATCHES_PAGE
    },
    matches_buttongroup_view_calendar_week_overview_and_click: makeRequiredClickStep({
        element: '[data-tour="matches_calendar_toggle_week"]',
        popover: {title: 'Week View', description: 'Let\'s see the week view', disableButtons: ['previous']},
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_calendar_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
            }
        },
        requireUserClick: true,
        waitForElement: 1500,
        route: '/' + MATCHES_PAGE
    }),
    matches_calendar_box_overview_view_week: {
        element: '[data-tour="matches_calendar_view_box"]',
        popover: {title: 'Week', description: 'For each event you can see the time interval, date and eventually, clicking on it, you\'ll navigate the editor of related contents', disableButtons: ['previous']},
        disableActiveInteraction: true,
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_calendar_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
                const table = await waitForElement('[data-tour="matches_calendar_toggle_week"]', 2000)
                if(table instanceof HTMLElement) {
                    table.click()
                }
            }
        },
        waitForElement: 500,
        route: '/' + MATCHES_PAGE
    },
    matches_buttongroup_view_calendar_day_overview_and_click: makeRequiredClickStep({
        element: '[data-tour="matches_calendar_toggle_day"]',
        popover: {title: 'Day View', description: 'Let\'s see the day view', disableButtons: ['previous']},
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_calendar_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
            }
        },
        requireUserClick: true,
        waitForElement: 1500,
        route: '/' + MATCHES_PAGE
    }),
    matches_calendar_box_overview_view_day: {
        element: '[data-tour="matches_calendar_view_box"]',
        popover: {title: 'Day', description: 'For each event you can see the time and eventually, clicking on it, you\'ll navigate the editor of related contents', disableButtons: ['previous']},
        disableActiveInteraction: true,
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_calendar_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
                const table = await waitForElement('[data-tour="matches_calendar_toggle_day"]', 2000)
                if(table instanceof HTMLElement) {
                    table.click()
                }
            }
        },
        waitForElement: 500,
        route: '/' + MATCHES_PAGE
    },
    matches_create_add_icon: {
        element: '[data-tour="matchboard_add_icon"]',
        popover: {title: 'Create Matchday', description: 'You can add a matchday through the current icon'},
        disableActiveInteraction: true,
        locationCheck: async () => {
            const but = await waitForElement('[data-tour="ingestion_matchboard_button"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
                await new Promise((resolve) => setTimeout(resolve, 400));
            }
        },
        waitForElement: 1000,
        //prima c'era anche un timeout resolve 1000 nel beforeNext
        requireUserClick: true,
        route: '/' + MATCHES_PAGE
    },
    matches_box_creation_of_matchday: {
        element: '[data-tour="matchday_single_box"][data-nomatchday="false"]',
        popover: {title: 'Name the Matchday', description: 'After clicking the icon, you\'ll be able to name the matchday and finally create it'},
        disableActiveInteraction: true,
        waitForElement: 2000,
        route: '/' + MATCHES_PAGE
    },

    users_card_user_card: {
        element: '[data-tour="users_user_card_total"]',
        popover: {title: 'User Profile', description: 'Here you can see and update your account informations'},
        disableActiveInteraction: true,
        route: '/' + USERS_PAGE
    },
    users_organization_grid_box: {
        element: '[data-tour="users_organization_grid"]',
        popover: {title: 'Organization', description: 'Here you can see your organization'},
        disableActiveInteraction: true,
        route: '/' + USERS_PAGE
    },
    users_information_grid_box: {
        element: '[data-tour="users_grid_informations"]',
        popover: {title: 'General Information', description: 'Here there are your general account informations'},
        disableActiveInteraction: true,
        route: '/' + USERS_PAGE
    },
    users_operations_informations: {
        element: '[data-tour="users_operations_account"]',
        popover: {title: 'Operations', description: 'Here you can execute different operations to manage your account'},
        disableActiveInteraction: true,
        route: '/' + USERS_PAGE
    },
    users_button_create_user_overview_and_click: {
        element: '[data-tour="users_create_user_button"]',
        popover: {title: 'Create User', description: 'Let\'s see the creational dialog for users'},
        disableActiveInteraction: true,
        requireUserClick:true,
        skipMissingElement: true,
        route: '/' + USERS_PAGE
    },
    users_dialog_creation_user: {
        element: '[data-tour="users_dialog_creation_user"]',
        popover: {title: 'User Infos', description: 'Here you can eventually describe the informations regarding your desired new account'},
        disableActiveInteraction: false,
        skipMissingElement: true,
        waitForElement: 1000,
        beforeNext: async () => {
            const but = await waitForElement('[data-tour="users_creational_form_close"]', 2000);
            if (but instanceof HTMLElement) {
                but.click();
            }
        },
        route: '/' + USERS_PAGE
    },
    users_datagrid_pro_total: {
        element: '[data-tour="users_data_grid_pro_total"]',
        popover: {title: 'Users', description: 'Here you can see all the available details about users'},
        disableActiveInteraction: true,
        skipMissingElement: true,
        waitForElement: 500,
        route: '/' + USERS_PAGE
    },
    users_datagrid_pro_edit_icon_overview: {
        element: '[data-tour="users_data_grid_pro_edit_icon"]',
        popover: {title: 'Edit', description: 'Here you can edit single or total informations about the user'},
        disableActiveInteraction: true,
        skipMissingElement: true,
        route: '/' + USERS_PAGE
    },
    users_datagrid_pro_change_password: {
        element: '[data-tour="users_datagrid_pro_change_password"]',
        popover: {title: 'Change password', description: 'Here you can change password of the desired account'},
        disableActiveInteraction: true,
        skipMissingElement: true,
        route: '/' + USERS_PAGE
    },
    users_datagrid_pro_delete_user: {
        element: '[data-tour="users_datagrid_pro_delete"]',
        popover: {title: 'Delete', description: 'Here you can delete the desired user'},
        disableActiveInteraction: true,
        skipMissingElement: true,
        route: '/' + USERS_PAGE
    },
    users_button_create_organization: {
        element: '[data-tour="users_button_create_organization"]',
        popover: {title: 'Create Organization', description: 'Let\'s see how to create a single organization'},
        disableActiveInteraction: true,
        skipMissingElement: true,
        requireUserClick: true,
        route: '/' + USERS_PAGE
    },
    users_overview_create_organization: {
        element: '[data-tour="users_organization_form_total_box"]',
        popover: {title: 'Organization Details', description: 'Here you can set the name, the related users, the competition and eventual specific teams available for the related users'},
        waitForElement: 2000,
        disableActiveInteraction: true,
        skipMissingElement: true,
        beforeNext: async() => {
            const but = await waitForElement('[data-tour="users_button_close_create_organization"]', 2000)
            if (but instanceof HTMLElement) {
                but.click();
            }
        },
        route: '/' + USERS_PAGE
    },
    users_overview_all_organizations: {
        element: '[data-tour="users_organization_total"]',
        popover: {title: 'Organizations', description: 'Here you can see all the available organization with related operations'},
        disableActiveInteraction: true,
        skipMissingElement: true,
        route: '/' + USERS_PAGE
    },
    users_overview_organization_datagrid_edit: {
        element: '[data-tour="users_organization_edit_icon"]',
        popover: {title: 'Edit Organization', description: 'Clicking on this icon you\'ll be able to edit the current organization'},
        disableActiveInteraction: true,
        skipMissingElement: true,
        route: '/' + USERS_PAGE
    },
    users_overview_organization_datagrid_delete: {
        element: '[data-tour="users_organization_delete_icon"]',
        popover: {title: 'Delete Organization', description: 'Here you can delete the current organization' },
        disableActiveInteraction: true,
        skipMissingElement: true,
        route: '/' + USERS_PAGE
    },
    users_overview_button_create_role: {
        element: '[data-tour="users_create_role_button"]',
        popover: {title: 'Create Role', description: 'Let\'s see how to create a role'},
        disableActiveInteraction: true,
        skipMissingElement: true,
        requireUserClick: true,
        route: '/' + USERS_PAGE
    },
    users_overview_new_rule_form: {
        element: '[data-tour="user_new_role_paper_initial"]',
        popover: {title: 'New Role', description: 'Here you should set all the important informations regarding this new role', disableButtons: ['previous']},
        disableActiveInteraction: false,
        skipMissingElement: true,
        waitForElement: 1500,
        route: '/' + ROLE_PAGE
    },
    users_overview_new_rule_form_roles_selector: {
        element: '[data-tour="users_role_permissions_setter_creational"]',
        popover: {title: 'Select Permissions', description: 'Here you can select all the eventual permissions for your new role'},
        disableActiveInteraction: false,
        skipMissingElement: true,
        route: '/' + ROLE_PAGE
    },
    users_overview_all_roles_datagrid: {
        element: '[data-tour="users_total_datagrid_pro_roles"]',
        popover: {title: 'Roles', description: 'Here you can see all your available roles'},
        disableActiveInteraction: true,
        skipMissingElement: true,
        route: '/' + USERS_PAGE
    },
    users_overview_all_roles_datagrid_edit_icon: {
        element: '[data-tour="users_role_datagrid_pro_edit_icon"]',
        popover: {title: 'Edit Role', description: 'Here you can edit your desired role'},
        disableActiveInteraction: true,
        skipMissingElement: true,
        route: '/' + USERS_PAGE
    },
    users_overview_all_roles_datagrid_delete_icon: {
        element: '[data-tour="users_role_datagrid_pro_delete_icon"]',
        popover: {title: 'Delete Role', description: 'Here you can delete your desired role'},
        disableActiveInteraction: true,
        skipMissingElement: true,
        route: '/' + USERS_PAGE
    },

    clip_overview_content_aggregation_total: {
        element: '[data-tour="content_aggregation_production_settings_grid_container"]',
        popover: {title: 'Production Settings', description: 'Here you can manage the production settings of your desired clip', align: 'center'},
        disableActiveInteraction: false,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + CLIP_SINGLE_PAGE
    },
    clip_overview_content_aggregation_choose_button: {
        element: '[data-tour="content_aggregation_choose_button"]',
        popover: {title: 'Choose Key Moments', description: 'Here you can choose different key moments for your clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + CLIP_SINGLE_PAGE
    },
    clip_overview_content_aggregation_edit_button: {
        element: '[data-tour="content_aggregation_edit_button"]',
        popover: {title: 'Edit Clip', description: 'The chosen key moments could be edited through this button'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + CLIP_SINGLE_PAGE
    },
    clip_overview_content_aggregation_save_button: {
        element: '[data-tour="content-aggregation-save-button"]',
        popover: {title: 'Save Clip', description: 'After the choose of key moments and eventual editing, you can save the current clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + CLIP_SINGLE_PAGE
    },
    clip_overview_content_aggregation_copy_button: {
        element: '[data-tour="content_aggregation_copy_creation_button"]',
        popover: {title: 'Create a Copy', description: 'Here you can create a copy of your current clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + CLIP_SINGLE_PAGE
    },
    clip_overview_content_aggregation_render_button: {
        element: '[data-tour="content_aggregation_render_button"]',
        popover: {title: 'Render Clip', description: 'Here you can start a job for rendering your clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + CLIP_SINGLE_PAGE
    },
    clip_overview_content_aggregation_publish_button: {
        element: '[data-tour="content_aggregation_publish_icon"]',
        popover: {title: 'Publish Clip', description: 'Here you can publish your current clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + CLIP_SINGLE_PAGE
    },


    content_aggregation_clear_parameters_match_highlight: {
        element: '[data-tour="content_aggregation_clear_button"]',
        waitForElement: 200,
        skipMissingElement: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE,
        locationCheck: async () => {
            return true;
        },
        onHighlightStarted: async (element: any, step: any, { handleNext }: any) => {
            try {
                const option = await waitForElement('[data-tour="content_aggregation_clear_button"]', 2000);

                if (option instanceof HTMLElement) {
                    const clickable = option.querySelector('button, input, .MuiButtonBase-root') || option;
                    (clickable as HTMLElement).click();
                }
            } catch (e) {
                console.warn("Bottone clear non trovato", e);
            }
            if (handleNext) {
                setTimeout(() => {
                    handleNext();
                }, 50);
            }
        },
    },
    match_highlight_auto_selection_box: {
        element: '[data-tour="match-highlight-header"]',
        popover: {title: 'Auto Selection', description: 'Here there are competitions, match and time parameters you can set'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE,
    },
    match_highlight_competition_text: makeRequiredInputStep({
        element: '[data-tour="match-highlight-filter-competition"]',
        popover: {title: 'Competition Selection', description: 'Here you can select your desired competition', side: 'right'},
        disableActiveInteraction: false,
        requireUserClick: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE,
    }),
    match_highlight_match_text: makeRequiredInputStep({
        element: '[data-tour="match-highlight-filter-match"]',
        popover: {title: 'Match Selection', description: 'Here you can select your desired match', side: 'right'},
        disableActiveInteraction: false,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE,
    }, true),
    match_highlight_duration_params: {
        element: '[data-tour="content-aggregation-time-params"]',
        popover: {title: 'Duration Params', description: 'Here you can select your desired duration params'},
        disableActiveInteraction: false,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },
    match_highlight_choose_key_moment_types: {
        element: '[data-tour="content-aggregation-choose-key-moments"]',
        popover: {title: 'Key Moment Types', description: 'Here you can choose the desired key moment types for your clip'},
        allowKeyboardControl: true,
        disableActiveInteraction: false,
        onHighlightStarted: (element : any) => {
            // Aggiunge un listener che blocca la freccia destra e sinistra
            const handleKeyDown = (event:any) => {
                if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
                    // Impedisce al Radio Group di cambiare l'opzione selezionata
                    event.preventDefault();
                    // Evita che la pressione del tasto faccia scattare altri listener nativi
                    event.stopPropagation();
                }
            };

            const targetEl = element.node || element;
            targetEl.addEventListener('keydown', handleKeyDown, true);

            // Salva la funzione di cleanup sull'elemento per rimuoverla quando lo step termina
            targetEl._driverKeydownHandler = handleKeyDown;
        },
        onDeselected: (element: any) => {
            // Rimuove il listener quando si esce da questo step
            const targetEl = element.node || element;
            if (targetEl._driverKeydownHandler) {
                targetEl.removeEventListener('keydown', targetEl._driverKeydownHandler, true);
                delete targetEl._driverKeydownHandler;
            }
        },
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },
    match_highlight_choose_key_moment_types_finalized: {
        element: '[data-tour="match-highlight-getkeymoments"]',
        popover: {title: 'Get Key Moments', description: 'After choosing your key moment types, you\'ll be able to get your key moment types'},
        disableActiveInteraction: false,
        advanceOnClick: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },
    match_highlight_overview_content_aggregation_total: {
        element: '[data-tour="content_aggregation_production_settings_grid_container"]',
        popover: {title: 'Production Settings', description: 'Here you can manage the production settings of your desired clip', align: 'center'},
        disableActiveInteraction: false,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },
    match_highlight_content_aggregation_assets_list_overview: {
        element: '[data-tour="content_aggregation_assets_list"]',
        popover: {title: 'Assets', description: 'Here you can choose the assets of your desired content, like opener, interleaves and post-rolls'},
        skipMissingElement: true,
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },
    match_highlight_content_aggregation_materialization_settings_overview: {
        element: '[data-tour="content_aggregation_materialization_settings_grid_complete"]',
        popover: {title: 'Materialization Settings', description: 'Here you can modify the materialization settings for your desired clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },
    match_highlight_content_aggregation_materialization_settings_aspect_ratio: {
        element: '[data-tour="content_aggregation_materialization_settings_aspect_ratio"]',
        popover: {title: 'Aspect Ratio', description: 'Here you can modify the aspect ratio of your desired clip', side: 'left'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },
    match_highlight_content_aggregation_materialization_settings_audio_track: {
        element: '[data-tour="content_aggregation_materialization_settings_audio_track"]',
        popover: {title: 'Audio Track', description: 'Here you can modify the audio track of your desired clip', side: 'left'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },
    match_highlight_content_aggregation_materialization_settings_audio_fading_box: {
        element: '[data-tour="content_aggregation_materialization_settings_audio_fading_box"]',
        popover: {title: 'Audio Fading', description: 'Here you can choose to enable or disable the audio fading of your desired clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },
    match_highlight_content_aggregation_materialization_settings_l_cut: {
        element: '[data-tour="content_aggregation_materialization_settings_l_cut"]',
        popover: {title: 'L-Cut', description: 'Here you can choose to enable or disable the L-Cut and manage it'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },
    match_highlight_overview_content_aggregation_choose_button: {
        element: '[data-tour="content_aggregation_choose_button"]',
        popover: {title: 'Choose Key Moments', description: 'Here you can choose different key moments for your clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },
    match_highlight_overview_content_aggregation_edit_button: {
        element: '[data-tour="content_aggregation_edit_button"]',
        popover: {title: 'Edit Clip', description: 'The chosen key moments could be edited through this button'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },
    match_highlight_overview_content_aggregation_save_button: {
        element: '[data-tour="content-aggregation-save-button"]',
        popover: {title: 'Save Clip', description: 'After the choose of key moments and eventual editing, you can save the current clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },
    match_highlight_overview_content_aggregation_copy_button: {
        element: '[data-tour="content_aggregation_copy_creation_button"]',
        popover: {title: 'Create a Copy', description: 'Here you can create a copy of your current clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },
    match_highlight_overview_content_aggregation_render_button: {
        element: '[data-tour="content_aggregation_render_button"]',
        popover: {title: 'Render Clip', description: 'Here you can start a job for rendering your clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },
    match_highlight_overview_content_aggregation_publish_button: {
        element: '[data-tour="content_aggregation_publish_icon"]',
        popover: {title: 'Publish Clip', description: 'Here you can publish your current clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE
    },


    content_aggregation_clear_parameters_goal_parade: {
        element: '[data-tour="content_aggregation_clear_button"]',
        waitForElement: 200,
        skipMissingElement: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE,
        locationCheck: async () => {
            return true;
        },
        onHighlightStarted: async (element: any, step: any, { handleNext }: any) => {
            try {
                const option = await waitForElement('[data-tour="content_aggregation_clear_button"]', 2000);

                if (option instanceof HTMLElement) {
                    const clickable = option.querySelector('button, input, .MuiButtonBase-root') || option;
                    (clickable as HTMLElement).click();
                }
            } catch (e) {
                console.warn("Bottone clear non trovato", e);
            }
            if (handleNext) {
                setTimeout(() => {
                    handleNext();
                }, 50);
            }
        },
    },
    goal_parade_auto_selection_box: {
        element: '[data-tour="goal-parade-header"]',
        popover: {title: 'Auto Selection', description: 'Here there are competitions, match and time parameters you can set'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_competition_text: makeRequiredInputStep({
        element: '[data-tour="goal-parade-filter-competition"]',
        popover: {title: 'Competition Selection', description: 'Here you can select your desired competition', side: 'right'},
        disableActiveInteraction: false,
        requireUserClick: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    }),
    goal_parade_matchday_text: makeRequiredInputStep({
        element: '[data-tour="goal-parade-filter-matchday"]',
        popover: {title: 'Match Day Selection', description: 'Here you can select your desired matchday', side: 'right'},
        disableActiveInteraction: false,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    }),
    goal_parade_duration_params: {
        element: '[data-tour="content-aggregation-time-params"]',
        popover: {title: 'Duration Params', description: 'Here you can select your desired duration params'},
        disableActiveInteraction: false,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_choose_key_moment_types: {
        element: '[data-tour="content-aggregation-choose-key-moments"]',
        popover: {title: 'Key Moment Types', description: 'Here you can choose the desired key moment types for your clip'},
        allowKeyboardControl: true,
        disableActiveInteraction: false,
        onHighlightStarted: (element : any) => {
            // Aggiunge un listener che blocca la freccia destra e sinistra
            const handleKeyDown = (event:any) => {
                if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
                    // Impedisce al Radio Group di cambiare l'opzione selezionata
                    event.preventDefault();
                    // Evita che la pressione del tasto faccia scattare altri listener nativi
                    event.stopPropagation();
                }
            };

            const targetEl = element.node || element;
            targetEl.addEventListener('keydown', handleKeyDown, true);

            // Salva la funzione di cleanup sull'elemento per rimuoverla quando lo step termina
            targetEl._driverKeydownHandler = handleKeyDown;
        },
        onDeselected: (element: any) => {
            // Rimuove il listener quando si esce da questo step
            const targetEl = element.node || element;
            if (targetEl._driverKeydownHandler) {
                targetEl.removeEventListener('keydown', targetEl._driverKeydownHandler, true);
                delete targetEl._driverKeydownHandler;
            }
        },
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_choose_key_moment_types_finalized: {
        element: '[data-tour="match-highlight-getkeymoments"]',
        popover: {title: 'Get Key Moments', description: 'After choosing your key moment types, you\'ll be able to get your key moment types'},
        disableActiveInteraction: false,
        advanceOnClick: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_overview_content_aggregation_total: {
        element: '[data-tour="content_aggregation_production_settings_grid_container"]',
        popover: {title: 'Production Settings', description: 'Here you can manage the production settings of your desired clip', align: 'center'},
        disableActiveInteraction: false,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_overview_total_assets_list: {
        element: '[data-tour="content_aggregation_assets_list"]',
        skipMissingElement: true,
        disableActiveInteraction: true,
        popover: {title: 'Assets', description: 'Here you can choose the assets of your desired content, like opener, interleaves and post-rolls'},
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_content_aggregation_materialization_settings_overview: {
        element: '[data-tour="content_aggregation_materialization_settings_grid_complete"]',
        popover: {title: 'Materialization Settings', description: 'Here you can modify the materialization settings for your desired clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_content_aggregation_materialization_settings_aspect_ratio: {
        element: '[data-tour="content_aggregation_materialization_settings_aspect_ratio"]',
        popover: {title: 'Aspect Ratio', description: 'Here you can modify the aspect ratio of your desired clip', side: 'left'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_content_aggregation_materialization_settings_audio_track: {
        element: '[data-tour="content_aggregation_materialization_settings_audio_track"]',
        popover: {title: 'Audio Track', description: 'Here you can modify the audio track of your desired clip', side: 'left'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_content_aggregation_materialization_settings_audio_fading_box: {
        element: '[data-tour="content_aggregation_materialization_settings_audio_fading_box"]',
        popover: {title: 'Audio Fading', description: 'Here you can choose to enable or disable the audio fading of your desired clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_content_aggregation_materialization_settings_l_cut: {
        element: '[data-tour="content_aggregation_materialization_settings_l_cut"]',
        popover: {title: 'L-Cut', description: 'Here you can choose to enable or disable the L-Cut and manage it'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_overview_content_aggregation_choose_button: {
        element: '[data-tour="content_aggregation_choose_button"]',
        popover: {title: 'Choose Key Moments', description: 'Here you can choose different key moments for your clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_overview_content_aggregation_edit_button: {
        element: '[data-tour="content_aggregation_edit_button"]',
        popover: {title: 'Edit Clip', description: 'The chosen key moments could be edited through this button'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_overview_content_aggregation_save_button: {
        element: '[data-tour="content-aggregation-save-button"]',
        popover: {title: 'Save Clip', description: 'After the choose of key moments and eventual editing, you can save the current clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_overview_content_aggregation_copy_button: {
        element: '[data-tour="content_aggregation_copy_creation_button"]',
        popover: {title: 'Create a Copy', description: 'Here you can create a copy of your current clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_overview_content_aggregation_render_button: {
        element: '[data-tour="content_aggregation_render_button"]',
        popover: {title: 'Render Clip', description: 'Here you can start a job for rendering your clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },
    goal_parade_overview_content_aggregation_publish_button: {
        element: '[data-tour="content_aggregation_publish_icon"]',
        popover: {title: 'Publish Clip', description: 'Here you can publish your current clip'},
        disableActiveInteraction: true,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE
    },


    custom_key_moment_creation_label_custom_collection: {
        element: '[data-tour="custom_collection"]',
        popover: {title: 'Creation', description: 'If a collection contains key moments, it will be able to create a custom key moment'},
        disableActiveInteraction: true,
        waitForElement: 500,
        beforeNext: async (element: HTMLElement) => {
            if (!(element instanceof HTMLElement)) return;

            // 1. Notifica il Box per attivare l'onMouseEnter di React
            element.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true, cancelable: true }));
            element.dispatchEvent(new MouseEvent('mouseover', { bubbles: true, cancelable: true }));

            // 2. Aspetta che React esegua il re-render e monti <Button data-tour="create_a_key_moment_button">
            await waitForElement('[data-tour="create_a_key_moment_button"]', 2000);
        },
        route: '/' + KEY_MOMENT_PAGE_NAME
    },
    custom_key_moment_creation_label_custom_collection_button: makeRequiredClickStep({
        element: '[data-tour="create_a_key_moment_button"]',
        popover: {title: 'Create Key Moment', description: 'Click here to proceed to the creational phase'},
        requireUserClick: true,
        advanceOnClick: true,
        waitForElement: 500,
        onHighlightStarted: (element?: Element) => {
            const card = document.querySelector('[data-tour="custom_collection"]');
            if (!card) return;

            // 1. Spariamo subito l'hover per essere sicuri che sia visibile
            card.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true, cancelable: true }));

            // 2. Manteniamo forzatamente lo stato di hover a 50ms per tutta la durata dello step.
            // Questo contrasta qualsiasi MouseLeave inviato da React quando il mouse si sposta verso il popover/pulsante.
            if ((card as any)._tourHoverInterval) {
                clearInterval((card as any)._tourHoverInterval);
            }

            (card as any)._tourHoverInterval = setInterval(() => {
                // Se per qualsiasi motivo il pulsante è venuto a mancare, riforciamo l'hover sulla card
                if (!document.querySelector('[data-tour="create_a_key_moment_button"]')) {
                    card.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true, cancelable: true }));
                    card.dispatchEvent(new MouseEvent('mouseover', { bubbles: true, cancelable: true }));
                }
            }, 50);
        },
        onDeselected: () => {
            const card = document.querySelector('[data-tour="custom_collection"]');
            if (card && (card as any)._tourHoverInterval) {
                // Clean-up dell'intervallo per ripristinare il comportamento normale del componente
                clearInterval((card as any)._tourHoverInterval);
                delete (card as any)._tourHoverInterval;

                // Inviamo un mouseleave finale per riportare la card allo stato normale
                card.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true, cancelable: true }));
            }
        },
        route: '/' + KEY_MOMENT_PAGE_NAME
    }),
    custom_key_moment_creation_box_panel_keymoment_form: {
        element: '[data-tour="metadata_panel_form"]',
        popover: {title: 'Panel Form', description: 'Before creating a custom key moment, you\'ll need to fill metadata', side: 'right'},
        disableActiveInteraction: true,
        waitForElement: 4000,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + EDITOR_PAGE
    },
    custom_key_moment_creation_box_timeline_div_total: {
        element: '[data-tour="timeline_div_total"]',
        popover: {title: 'Timeline', description: 'Here\'s the header of the timeline of the current match. Here you can save a key moment, reframe and navigate through the centered arrows'},
        disableActiveInteraction: true,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + EDITOR_PAGE
    },
    custom_key_moment_creation_timeline_div_total_tracks: {
        element: '[data-tour="timeline_div_total_tracks"]',
        popover: {title: 'Tracks', description: 'Here are the audio and video tracks with different key moments'},
        disableActiveInteraction: true,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + EDITOR_PAGE
    },


    navigate_to_calendar: {
        route: '/' + MATCHES_PAGE,
        element: '',
        locationCheck: async () => {
            return true;
        }
    },
    navigate_to_key_moment: {
      route: '/' + KEY_MOMENT_PAGE_NAME,
      element: '',
      locationCheck: async () => {
          return true;
      }
    },
    navigate_to_key_moment_library: {
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        element: '',
        locationCheck: async () => {
            return true;
        }
    },
    navigate_to_clips: {
        route: '/' + CLIP_PAGE_NAME,
        element: '',
        locationCheck: async () => {
            return true;
        }
    },
    navigate_to_monitor_page: {
        route: '/' + AGENT_PAGE,
        element: '',
        locationCheck: async () => {
            return true;
        }
    },
    navigate_to_rules: {
        route: '/' + RULES_PAGE,
        element: '',
        locationCheck: async () => {
            return true;
        }
    },
    navigate_to_social: {
        route: '/' + SOCIAL_PAGE,
        element: '',
        locationCheck: async () => {
            return true;
        }
    },
    navigate_to_users: {
        route: '/' + USERS_PAGE,
        element: '',
        locationCheck: async () => {
            return true;
        }
    },
    navigate_to_dashboard: {
        route: '/' + DASHBOARD_PAGE,
        element: '',
        locationCheck: async () => {
            return true;
        }
    },
    navigate_to_match_highlight: {
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE,
        element: '',
        locationCheck: async () => {
            return true;
        }
    },
    navigate_to_goal_parade: {
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE,
        element: '',
        locationCheck: async () => {
            return true;
        }
    },
    navigate_to_clip_creation: {
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + CLIP_SINGLE_PAGE,
        element: '',
        locationCheck: async () => {
            return true;
        }
    },


    key_moments_required_showing_user_first_field: {
        element: '[data-tour="filter_accordion_field_search"]',
        disableActiveInteraction: false,
        waitForElement: 1000,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        onHighlightStarted: (element : any, step : any, {handleNext}: any) => {
            element.click()
            handleNext()
        }
    },
    key_moments_required_user_compiling_first_field_search_library: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_search_library"]',
        popover: {title: 'Search', description: 'Here you can execute a global search on all available cards'},
        waitForElement: 1000,
        disableActiveInteraction:true,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        requireUserClick: true,
    }),

    key_moments_required_showing_user_competition_field: {
        element: '[data-tour="filter_accordion_field_competition"]',
        disableActiveInteraction: true,
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        onHighlightStarted: (element : any, step : any, {handleNext}: any) => {
            element.click()
            handleNext()
        }
    },

    key_moments_required_user_compiling_competition_field_library: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_competitionFilterValues_library"]',
        disableActiveInteraction: false,
        popover: {title: 'Competition', description: 'Here you can search by competition'},
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        requireUserClick: true
    }),

    key_moments_required_user_compiling_competition_checkboxes_field_library: makeRequiredClickStep({
        element: '[data-tour="text_field_checkboxes_filter_competitionFilterValues_library"]',
        popover: {title: 'Competition', description: 'Checking desired competitions...'},
        disableActiveInteraction: false,
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    }),

    key_moments_required_showing_user_match_field: {
        element: '[data-tour="filter_accordion_field_match"]',
        disableActiveInteraction: true,
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        onHighlightStarted: (element : any, step : any, {handleNext}: any) => {
            element.click()
            handleNext()
        }
    },

    key_moments_required_user_compiling_match_field_library: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_matchFilterValues_library"]',
        disableActiveInteraction: false,
        popover: {title: 'Match', description: 'Here you can search by match'},
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        requireUserClick: true
    }),

    key_moments_required_user_compiling_match_checkboxes_field_library: makeRequiredClickStep({
        element: '[data-tour="text_field_checkboxes_filter_matchFilterValues_library"]',
        disableActiveInteraction: false,
        popover: {title: 'Match', description: 'Checking desired matches...'},
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME
    }),

    key_moments_required_showing_user_datetime_field: {
        element: '[data-tour="filter_accordion_field_startSchedule"]',
        disableActiveInteraction: true,
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        onHighlightStarted: (element : any, step : any, {handleNext}: any) => {
            element.click()
            handleNext()
        }
    },

    key_moments_required_user_compiling_startSchedule_field_library: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_eventDate_library"]',
        disableActiveInteraction: false,
        popover: {title: 'Datetime', description: 'Here you can search by datetime'},
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        requireUserClick: true
    }),

    key_moments_required_showing_user_keymoment_field: {
        element: '[data-tour="filter_accordion_field_key_moment_type"]',
        disableActiveInteraction: true,
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        onHighlightStarted: (element : any, step : any, {handleNext}: any) => {
            element.click()
            handleNext()
        }
    },

    key_moments_required_user_compiling_keymoment_field_library: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_keyMomentTypeValues_library"]',
        popover: {title: 'Key Moment', description: 'Here you can search for your key moments'},
        disableActiveInteraction:false,
        waitForElement: 1500,
        route: '/'+ KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        requireUserClick: true
    }),

    key_moments_required_user_compiling_keymoments_checkboxes_field_library: makeRequiredClickStep({
        element: '[data-tour="text_field_checkboxes_filter_keyMomentTypeValues_library"]',
        popover: {title: 'Key moments', description: 'Checking desired key moments...'},
        disableActiveInteraction: false,
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
    }),

    key_moments_required_showing_user_created_at_field: {
        element: '[data-tour="filter_accordion_field_created_at"]',
        disableActiveInteraction: true,
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        onHighlightStarted: (element : any, step : any, {handleNext}: any) => {
            element.click()
            handleNext()
        }
    },

    key_moments_required_user_compiling_production_date_field_library: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_productionDate_library"]',
        popover: {title: 'Production Date', description: 'Choose your desired production date interval'},
        disableActiveInteraction:false,
        waitForElement: 1500,
        route: '/'+ KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        requireUserClick: true
    }),



    key_moments_required_showing_user_player_field: {
        element: '[data-tour="filter_accordion_field_player_search"]',
        disableActiveInteraction: true,
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        onHighlightStarted: (element : any, step : any, {handleNext}: any) => {
            element.click()
            handleNext()
        }
    },
    key_moments_required_user_compiling_player_field_library: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_playerSearch_library"]',
        disableActiveInteraction: false,
        popover: {title: 'Player', description: 'Here you can search by player'},
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        requireUserClick: true
    }),
    key_moments_required_user_compiling_player_checkboxes_field_library: makeRequiredClickStep({
        element: '[data-tour="text_field_checkboxes_filter_playerSearch_library"]',
        popover: {title: 'Player', description: 'Checking desired players'},
        disableActiveInteraction: false,
        waitForElement: 1500,
        route: '/'+ KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
    }),

    key_moments_required_showing_user_period_field: {
        element: '[data-tour="filter_accordion_field_period"]',
        disableActiveInteraction: true,
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        onHighlightStarted: (element : any, step : any, {handleNext}: any) => {
            element.click()
            handleNext()
        }
    },
    key_moments_required_user_compiling_period_field_library: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_periodFilterValues_library"]',
        disableActiveInteraction: false,
        popover: {title: 'Period', description: 'Here you can search by period'},
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        requireUserClick: true
    }),
    key_moments_required_user_compiling_period_checkboxes_field_library: makeRequiredClickStep({
       element: '[data-tour="text_field_checkboxes_filter_periodFilterValues_library"]',
       popover: {title: 'Period', description: 'Checking desired periods...'},
       disableActiveInteraction: false,
       waitForElement: 1500,
       route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
    }),


    key_moments_required_showing_user_matchday_field: {
        element: '[data-tour="filter_accordion_field_matchday"]',
        disableActiveInteraction: true,
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        onHighlightStarted: (element : any, step : any, {handleNext}: any) => {
            element.click()
            handleNext()
        }
    },
    key_moments_required_user_compiling_matchday_field_library: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_matchDayFilterValues_library"]',
        disableActiveInteraction: false,
        popover: {title: 'Matchday', description: 'Here you can search by matchday'},
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        requireUserClick: true
    }),

    key_moments_required_user_compiling_matchday_checkboxes_field_library: makeRequiredClickStep({
        element: '[data-tour="text_field_checkboxes_filter_matchDayFilterValues_library"]',
        popover: {title: 'Matchday', description: 'Checking desired matchdays'},
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        disableActiveInteraction: false
    }),

    key_moments_required_showing_user_tags_field: {
        element: '[data-tour="filter_accordion_field_tags"]',
        disableActiveInteraction: true,
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        onHighlightStarted: (element : any, step : any, {handleNext}: any) => {
            element.click()
            handleNext()
        }
    },

    key_moments_required_user_compiling_tags_field_library: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_tags_library"]',
        disableActiveInteraction: false,
        popover: {title: 'Tags', description: 'Here you can search by tags'},
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        requireUserClick: true
    }),

    key_moments_required_showing_user_rating_field: {
        element: '[data-tour="filter_accordion_field_rating"]',
        disableActiveInteraction: true,
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        onHighlightStarted: (element : any, step : any, {handleNext}: any) => {
            element.click()
            handleNext()
        }
    },
    key_moments_required_user_compiling_rating_field_library: makeRequiredClickStep({
        element: '[data-tour="text_field_debounced_text_filter_ratingFilter_library"]',
        disableActiveInteraction: false,
        popover: {title: 'Rating', description: 'Here you can search by rating of your key moments'},
        waitForElement: 1500,
        route: '/' + KEY_MOMENT_PAGE_NAME + '/' + LIBRARY_PAGE_NAME,
        requireUserClick: true
    }),

    match_highlight_set_minimum_slider_duration: makeRequiredClickStep({
        element: '[data-tour="match_highlight_min_slider"]',
        popover: {title: 'Minimum Duration', description: 'Set minimum duration for your content'},
        disableActiveInteraction: false,
        waitForElement: 1500,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE,
        requireUserClick: true
    }),

    match_highlight_get_key_moments_button_click: {
        element: '[data-tour="match-highlight-getkeymoments"]',
        disableActiveInteraction:false,
        waitForElement: 500,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE,
        onHighlightStarted: async (element :any) => {
            element.click()
        }
    },

    goal_parade_get_key_moments_button_click: {
        element: '[data-tour="match-highlight-getkeymoments"]',
        disableActiveInteraction:false,
        waitForElement: 500,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE,
        onHighlightStarted: async (element :any) => {
            element.click()
        }
    },


    match_highlight_set_maximum_slider_duration: makeRequiredClickStep({
        element: '[data-tour="match_highlight_max_slider"]',
        popover: {title: 'Maximum Duration', description: 'Set maximum duration for your content'},
        disableActiveInteraction: false,
        waitForElement: 1500,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE,
        requireUserClick: true
    }),

    goal_parade_set_minimum_slider_duration: makeRequiredClickStep({
        element: '[data-tour="match_highlight_min_slider"]',
        popover: {title: 'Minimum Duration', description: 'Set minimum duration for your content'},
        disableActiveInteraction: false,
        waitForElement: 1500,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE,
        requireUserClick: true
    }),

    goal_parade_set_maximum_slider_duration: makeRequiredClickStep({
        element: '[data-tour="match_highlight_max_slider"]',
        popover: {title: 'Maximum Duration', description: 'Set maximum duration for your content'},
        disableActiveInteraction: false,
        waitForElement: 1500,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + GOAL_PARADE_PAGE,
        requireUserClick: true
    }),

    match_highlight_key_moment_included_start: makeRequiredClickStep({
       element: '[data-tour="content_aggregation_include_START"]',
       popover: {title: 'Start', description: 'Decide if there will be the start of match inside the desired content'},
       waitForElement: 500,
       route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE,
       requireUserClick: true,
       disableActiveInteraction: false
    }),

    match_highlight_key_moment_included_goal: makeRequiredClickStep({
        element: '[data-tour="content_aggregation_include_GOAL"]',
        popover: {title: 'Goal', description: 'Decide if there will be goals inside the desired content'},
        waitForElement: 500,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE,
        requireUserClick: true,
        disableActiveInteraction: false
    }),
    match_highlight_key_moment_included_save: makeRequiredClickStep({
        element: '[data-tour="content_aggregation_include_SAVE"]',
        popover: {title: 'Save', description: 'Decide if there will be saves inside the desired content'},
        waitForElement: 500,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE,
        requireUserClick: true,
        disableActiveInteraction: false
    }),
    match_highlight_key_moment_included_miss: makeRequiredClickStep({
        element: '[data-tour="content_aggregation_include_MISS"]',
        popover: {title: 'Miss', description: 'Decide if there will be misses inside the desired content'},
        waitForElement: 500,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE,
        requireUserClick: true,
        disableActiveInteraction: false
    }),
    match_highlight_key_moment_included_card: makeRequiredClickStep({
        element: '[data-tour="content_aggregation_include_CARD"]',
        popover: {title: 'Card', description: 'Decide if there will be cards inside the desired content'},
        waitForElement: 500,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE,
        requireUserClick: true,
        disableActiveInteraction: false
    }),
    match_highlight_key_moment_included_end: makeRequiredClickStep({
        element: '[data-tour="content_aggregation_include_END"]',
        popover: {title: 'End', description: 'Decide if there will be the end of the game inside the desired content'},
        waitForElement: 500,
        route: '/' + CONTENT_AGGREGATION_PAGE_NAME + '/' + MATCH_HIGHLIGHT_PAGE,
        requireUserClick: true,
        disableActiveInteraction: false
    }),


    content_aggregation_key_moment_setting_aspect_ratio_showing_and_click: makeRequiredClickStep({
        element: '[data-tour="content_aggregation_materialization_settings_clickable_aspect_ratio"]',
        waitForElement: 500,
        requireUserClick: true,
        disableActiveInteraction: false,
        onHighlightStarted: (element : any, step : any, {handleNext}: any) => {
            element.click()
            handleNext()
        }
    }),



    clips_open_view_all_without_collections_filtered: {
        //Giusto perchè lo si vede sempre
        element: '[data-tour="quick_actions"]',
        waitForElement: 1000,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element: any, step : any, {handleNext}) => {new Promise(resolve => setTimeout(resolve, 600)); window.dispatchEvent(new Event('tourFireClips', {bubbles:true})); handleNext()}
    },
    clips_view_all_open_filter_drawer_automatically: {
        element: '[data-tour="filters_drawer"]',
        waitForElement: 1500,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element:any, step:any, {handleNext}) => {
            element.click()
            handleNext()
        }
    },
    clips_view_all_filter_drawer_global_search_overview_and_click: {
        element: '[data-tour="filter_accordion_field_search"]',
        waitForElement: 1000,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element:any, step:any, {handleNext}) => {
            element.click()
            handleNext()
        }
    },

    clips_view_all_filter_drawer_reset_button_click: {
        element: '[data-tour="filter_drawer_reset_button"]',
        waitForElement: 1500,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element:any, step:any, {handleNext}) => {
            element.click();
            handleNext()
        }
    },

    clips_view_all_filter_drawer_search_label_on_writing: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_search_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: false
    }),

    clips_view_all_filter_drawer_competition_overview_and_click: {
        element: '[data-tour="filter_accordion_field_competition"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element:any, step:any, {handleNext}) => {
            element.click();
            handleNext()
        }
    },

    clips_view_all_filter_drawer_competition_label_on_writing: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_competitionFilterValues_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: false
    }),
    clips_view_all_filter_drawer_competition_label_checkbox_compiling: makeRequiredClickStep({
        element: '[data-tour="text_field_checkboxes_filter_competitionFilterValues_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: true,
        beforeNext: async() => {
            await new Promise(resolve => setTimeout(resolve, 500));
        }
    }),


    clips_view_all_filter_drawer_match_overview_and_click: {
        element: '[data-tour="filter_accordion_field_match"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element:any, step:any, {handleNext}) => {
            element.click();
            handleNext()
        }
    },

    clips_view_all_filter_drawer_match_label_on_writing: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_matchFilterValues_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: false
    }),
    clips_view_all_filter_drawer_match_label_checkbox_compiling: makeRequiredClickStep({
        element: '[data-tour="text_field_checkboxes_filter_matchFilterValues_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: true
    }),



    clips_view_all_filter_drawer_match_date_overview_and_click: {
        element: '[data-tour="filter_accordion_field_startSchedule"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element:any, step:any, {handleNext}) => {
            element.click();
            handleNext()
        }
    },
    clips_view_all_filter_drawer_match_date_label_on_writing: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_eventDate_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: false
    }),

    clips_view_all_filter_drawer_key_moment_type_overview_and_click: {
        element: '[data-tour="filter_accordion_field_key_moment_type"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element:any, step:any, {handleNext}) => {
            element.click();
            handleNext()
        }
    },
    clips_view_all_filter_drawer_key_moment_type_label_on_writing: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_keyMomentTypeValues_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: false
    }),
    clips_view_all_filter_drawer_key_moment_type_label_checkbox_compiling: makeRequiredClickStep({
        element: '[data-tour="text_field_checkboxes_filter_keyMomentTypeValues_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: true
    }),

    clips_view_all_filter_drawer_player_overview_and_click: {
        element: '[data-tour="filter_accordion_field_player_search"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element:any, step:any, {handleNext}) => {
            element.click();
            handleNext()
        }
    },
    clips_view_all_filter_drawer_player_label_on_writing: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_playerSearch_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: false
    }),
    clips_view_all_filter_drawer_player_label_checkbox_compiling: makeRequiredClickStep({
        element: '[data-tour="text_field_checkboxes_filter_playerSearch_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: true
    }),


    clips_view_all_filter_drawer_created_at_overview_and_click: {
        element: '[data-tour="filter_accordion_field_created_at"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element:any, step:any, {handleNext}) => {
            element.click();
            handleNext()
        }
    },
    clips_view_all_filter_drawer_production_date_label_on_writing: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_productionDate_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: false
    }),

    clips_view_all_filter_drawer_tags_overview_and_click: {
        element: '[data-tour="filter_accordion_field_tags"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element:any, step:any, {handleNext}) => {
            element.click();
            handleNext()
        }
    },
    clips_view_all_filter_drawer_tags_label_on_writing: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_tags_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: false
    }),

    clips_view_all_filter_drawer_period_overview_and_click: {
        element: '[data-tour="filter_accordion_field_period"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element:any, step:any, {handleNext}) => {
            element.click();
            handleNext()
        }
    },
    clips_view_all_filter_drawer_period_label_on_writing: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_periodFilterValues_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: false
    }),
    clips_view_all_filter_drawer_period_label_checkbox_compiling: makeRequiredClickStep({
        element: '[data-tour="text_field_checkboxes_filter_periodFilterValues_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: true
    }),


    clips_view_all_filter_drawer_rating_overview_and_click: {
        element: '[data-tour="filter_accordion_field_rating"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element:any, step:any, {handleNext}) => {
            element.click();
            handleNext()
        }
    },
    clips_view_all_filter_drawer_rating_label_on_writing: makeRequiredClickStep({
        element: '[data-tour="text_field_debounced_text_filter_ratingFilter_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: false
    }),


    clips_view_all_filter_drawer_match_day_overview_and_click: {
        element: '[data-tour="filter_accordion_field_matchday"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element:any, step:any, {handleNext}) => {
            element.click();
            handleNext()
        }
    },
    clips_view_all_filter_drawer_match_day_label_on_writing: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_matchDayFilterValues_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: false
    }),
    clips_view_all_filter_drawer_match_day_label_checkbox_compiling: makeRequiredClickStep({
        element: '[data-tour="text_field_checkboxes_filter_matchDayFilterValues_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: true
    }),


    clips_view_all_filter_drawer_aspect_ratio_overview_and_click: {
        element: '[data-tour="filter_accordion_field_aspect_ratio"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element:any, step:any, {handleNext}) => {
            element.click();
            handleNext()
        }
    },
    clips_view_all_filter_drawer_aspect_ratio_label_on_writing: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_aspectRatioFilterValues_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: false
    }),
    clips_view_all_filter_drawer_aspect_ratio_label_checkbox_compiling: makeRequiredClickStep({
        element: '[data-tour="text_field_checkboxes_filter_aspectRatioFilterValues_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: true
    }),


    clips_view_all_filter_drawer_content_type_overview_and_click: {
        element: '[data-tour="filter_accordion_field_type"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        onHighlightStarted: (element:any, step:any, {handleNext}) => {
            element.click();
            handleNext()
        }
    },
    clips_view_all_filter_drawer_content_type_label_on_writing: makeRequiredInputStep({
        element: '[data-tour="text_field_debounced_text_filter_contentTypeFilterValues_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: false
    }),
    clips_view_all_filter_drawer_content_type_label_checkbox_compiling: makeRequiredClickStep({
        element: '[data-tour="text_field_checkboxes_filter_contentTypeFilterValues_library"]',
        waitForElement: 500,
        route: '/' + CLIP_PAGE_NAME,
        requireUserClick: true,
        disableActiveInteraction: true
    }),

}


export const TourRouteDescription : Record<string, string> = {
    '/rules': 'This page contains the lists of event-based and time-based triggers for automatic creation of contents.',
    '/keymoments': 'This page contains all the key moments collections available in your organization and account with the possibility of editing and visualizing them.',
    '/clips': 'This page contains all the clips collections available in your organization and account with the possibility of editing and visualizing them.',
    '/library': 'This page contains all key moments present in your organization and account with the possibility of visualizing, filtering and editing them.',
    '/automation_forge': 'This page allows the creation and update of a trigger to save render or publish a clip.',
    '/renderingjob': 'This page contains all types of rendering Agents, like rendering of clip contents and scheduled Ingestion and Extraction Agents.'
}


/* TUTORIAL BE LIKE:*/
/*beforeNext: async (element : any) => {
                if(element instanceof HTMLElement) {
                    //const input = (element.querySelector('input') || element) as HTMLInputElement; POTEVAMO FARE QUESTO MA NON LO FACCIAMO, ME LO MEMORIZZO
                    element.focus()

                    simulateTyping(element, "")

                    simulateTyping(element, "Italian")
                    await new Promise(resolve => setTimeout(resolve, 1000));

                    const optionElement = await waitForElement('[data-id="Italian Serie B - 2025"]', 30000);

                    if (optionElement instanceof HTMLElement) {
                        optionElement.click();
                    }
                }
            }
**/